import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, MapPin as MapPinIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Fix Leaflet default marker icon in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const CATEGORIES = [
  "boundary", "entrance", "building", "poultry_house", "storage", "tank",
  "solar", "generator", "borehole", "road", "parking", "future",
];

export default function PropertyMap() {
  const [prop, setProp] = useState(null);
  const [features, setFeatures] = useState([]);
  async function refresh() {
    const [p, f] = await Promise.all([api.get("/property"), api.get("/map/features")]);
    setProp(p.data); setFeatures(f.data);
  }
  useEffect(() => { refresh(); }, []);
  if (!prop) return <div className="p-8 subtle">Loading property map…</div>;
  const center = [prop.latitude ?? -21.365, prop.longitude ?? 30.905];

  return (
    <div className="space-y-6" data-testid="map-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Digital Homestead</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight">Property Map</h1>
        <p className="subtle mt-1">Foundation for the future Digital Twin — {prop.name}.</p>
      </div>

      <Card className="rounded-2xl overflow-hidden">
        <CardContent className="p-0">
          <MapContainer center={center} zoom={16} style={{ height: 520, width: "100%" }}>
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={center}>
              <Popup>
                <div className="font-medium">{prop.name}</div>
                <div className="text-xs">{prop.address_line1}, {prop.town}</div>
                <div className="text-xs">Property ID: {prop.id}</div>
              </Popup>
            </Marker>
            {features.filter((f) => f.latitude && f.longitude).map((f) => (
              <Marker key={f.id} position={[f.latitude, f.longitude]}>
                <Popup>
                  <div className="font-medium">{f.name}</div>
                  <div className="text-xs uppercase tracking-widest">{f.category}</div>
                  <div className="text-xs">{f.description}</div>
                  {!f.verified && <div className="text-[10px] text-red-600 mt-1">Approximate — verification required</div>}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div><CardTitle>Mapped features ({features.length})</CardTitle><CardDescription>Buildings, infrastructure and future areas.</CardDescription></div>
          <NewFeature center={center} onCreated={refresh} />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {features.map((f) => (
              <div key={f.id} className="rounded-xl border border-border p-3">
                <div className="flex items-center justify-between">
                  <div className="font-medium">{f.name}</div>
                  <Badge variant="outline">{f.category}</Badge>
                </div>
                <div className="text-xs subtle mt-1">{f.latitude?.toFixed(5)}, {f.longitude?.toFixed(5)}</div>
                <div className="text-xs mt-1">{f.description}</div>
                {!f.verified && <div className="text-[10px] text-destructive mt-2">Approximate — verification required</div>}
              </div>
            ))}
            {features.length === 0 && <div className="col-span-full subtle text-center py-8">No features mapped yet.</div>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function NewFeature({ center, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", category: "building", latitude: center[0], longitude: center[1],
    description: "", responsible_person: "", status: "active", verified: false, notes: "",
  });
  async function submit() {
    try {
      await api.post("/map/features", {
        ...form, latitude: Number(form.latitude), longitude: Number(form.longitude),
      });
      toast.success("Feature added"); setOpen(false); onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-deepgreen hover:bg-deepgreen/90" data-testid="new-feature-btn"><Plus className="h-4 w-4 mr-1" />New feature</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New mapped feature</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Responsible</Label><Input value={form.responsible_person} onChange={(e) => setForm({ ...form, responsible_person: e.target.value })} /></div>
          <div><Label>Latitude</Label><Input type="number" step="0.00001" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} /></div>
          <div><Label>Longitude</Label><Input type="number" step="0.00001" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} /></div>
          <div className="col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={submit}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
